import { Dropdown, RadioButton, RadioButtonGroup } from '@designsforhealth/dfh-react-components'
import React, { useCallback, useMemo } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import styled from 'styled-components'

import useSiteMetadata from '../../../hooks/useSiteMetadata'
import useSubmitFormOnDirty from '../../../hooks/useSubmitFormOnDirty'
import { OrderType } from '../../../lib/cart/item'
import { autoShipIntervalOptions } from '../../../lib/cart/item-control-options'
import { CartItem } from '../../../lib/cart/types'
import { useShowAutoShipAndSaveToUserGroup } from '../../../lib/m2/user-groups'
import { useTestKitProducts } from '../../../lib/products/hooks/useTestKitProducts'
import { logAndCaptureException } from '../../../utils/errorTools'
import UpdateCartItemErrorAlert from '../../cart/UpdateCartItemErrorAlert'
import { AutoShipControlLabel } from './AutoShipControlLabel'

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  ${RadioButton} label .icon {
    flex-shrink: 0;
  }
`

export interface CartItemOrderTypeControlProps {
  className?: string
  disabled: boolean
  item: CartItem

  updateCartItem(props: {
    autoShipInterval?: number
    orderType?: number
  }): Promise<{ error?: Error }>
}

interface FormValues {
  orderType?: string
  autoShipInterval?: { label: string; value: string }
}

const CartItemOrderTypeControl: React.FC<CartItemOrderTypeControlProps> = ({
  className,
  disabled,
  item,
  updateCartItem,
}) => {
  const { featureFlags } = useSiteMetadata() ?? {}
  const showAutoShipAndSaveToUserGroup = useShowAutoShipAndSaveToUserGroup()
  const showAutoShipAndSave = !!featureFlags?.autoShipAndSave && showAutoShipAndSaveToUserGroup
  const defaultOrderType = useMemo(() => {
    const itemOrderType = item.item_attributes?.dfh_order_type
    switch (itemOrderType) {
      case OrderType.SingleOrder:
        return OrderType.SingleOrder
      case OrderType.AutoShip:
        return OrderType.AutoShip
      default:
        return OrderType.SingleOrder
    }
  }, [item.item_attributes?.dfh_order_type])

  const defaultAutoShipIntervalOption = useMemo(() => {
    const itemAutoShipInterval = item.item_attributes?.dfh_autoship_interval?.toString() || '1'
    return autoShipIntervalOptions.find(({ value }) => value === itemAutoShipInterval)
  }, [item.item_attributes?.dfh_autoship_interval])

  const { control, errors, formState, handleSubmit, register, reset, setError, watch } =
    useForm<FormValues>({
      mode: 'onChange',
    })

  const currentOrderType = watch('orderType', defaultOrderType.toString())

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    async ({ autoShipInterval, orderType }) => {
      const autoShipIntervalNumber = Number(autoShipInterval?.value)
      const orderTypeNumber = Number(orderType)

      // parse string values as numbers for comparison with cart data
      if (Number.isNaN(orderTypeNumber)) {
        const parseError = new Error(`Unable to parse orderType "${orderType}" as number`)
        logAndCaptureException(parseError)
        setError('orderType', { message: parseError.message })
        return
      }
      if (orderTypeNumber === OrderType.AutoShip && Number.isNaN(autoShipIntervalNumber)) {
        const parseError = new Error(
          `Unable to parse autoShipInterval "${autoShipInterval}" as number`,
        )
        logAndCaptureException(parseError)
        setError('autoShipInterval', { message: parseError.message })
        return
      }

      const itemOrderType = item.item_attributes?.dfh_order_type
      const itemAutoShipInterval = item.item_attributes?.dfh_autoship_interval

      // avoid update orderType and autoShipInterval match cart item attributes
      if (
        orderTypeNumber === itemOrderType &&
        (orderTypeNumber === OrderType.SingleOrder ||
          (orderTypeNumber === OrderType.AutoShip &&
            autoShipIntervalNumber === itemAutoShipInterval))
      ) {
        return
      }

      const result = await updateCartItem({
        orderType: orderTypeNumber,
        ...(orderTypeNumber === OrderType.AutoShip && {
          autoShipInterval: autoShipIntervalNumber,
        }),
      })

      if (result.error) {
        setError('orderType', { message: result.error.message, type: 'server' })
      }
    },
    [
      item.item_attributes?.dfh_autoship_interval,
      item.item_attributes?.dfh_order_type,
      setError,
      updateCartItem,
    ],
  )

  const { resetForm } = useSubmitFormOnDirty({
    defaultValues: useMemo(
      () => ({
        orderType: defaultOrderType.toString(),
        ...(defaultOrderType === OrderType.AutoShip && {
          autoShipInterval: defaultAutoShipIntervalOption,
        }),
      }),
      [defaultAutoShipIntervalOption, defaultOrderType],
    ),
    formState,
    handleSubmit,
    onSubmit,
    reset,
  })

  const { isTestKitSku } = useTestKitProducts()
  const showAutoShip = !isTestKitSku(item.product.sku || '')

  if (!showAutoShip) {
    return null
  }
  return (
    <div className={className}>
      {errors.orderType && (
        <UpdateCartItemErrorAlert onCancel={resetForm} onRetry={handleSubmit(onSubmit)} />
      )}
      <StyledRadioButtonGroup
        defaultValue={defaultOrderType.toString()}
        name="orderType"
        // The options must have unique ids, or else the RadioButtonGroup does not work when there
        // are multiple items on the DOM.
        radioButtonOptions={[
          {
            id: `${item.id}_single_order`,
            label: 'Single Order',
            value: OrderType.SingleOrder.toString(),
          },
          {
            id: `${item.id}_auto_ship`,
            label: (
              <AutoShipControlLabel
                className="auto-ship-label-cart"
                showSaveText={showAutoShipAndSave}
                disabled={disabled}
              />
            ),
            value: OrderType.AutoShip.toString(),
          },
        ]}
        register={register}
        disabled={disabled}
      />
      {currentOrderType !== OrderType.AutoShip.toString() ? (
        <p className="small-paragraph small-margin-bottom">
          {showAutoShipAndSave
            ? 'Set up Auto-Ship & Save 5% on this product. Your Auto-Ship can be canceled at any time.'
            : 'Set up Auto-Ship and never run out of the supplements you love! Cancel at any time.'}
        </p>
      ) : (
        <>
          {showAutoShipAndSave && (
            <p className="small-paragraph small-margin-bottom">
              You will receive a 5% discount on this product.
            </p>
          )}
          <Dropdown
            control={control}
            defaultValue={defaultAutoShipIntervalOption}
            name="autoShipInterval"
            options={autoShipIntervalOptions}
            prompt="Ship every:"
            disabled={disabled}
          />
        </>
      )}
    </div>
  )
}

const StyledCartItemOrderTypeControl = styled(CartItemOrderTypeControl)({
  [Dropdown]: {
    width: '175px',
  },
  [RadioButtonGroup]: {
    label: {
      minWidth: 'unset',
    },
  },
  '@media(min-width: 350px)': {
    [RadioButtonGroup]: {
      label: {
        minWidth: '119px',
      },
    },
  },
})

export default StyledCartItemOrderTypeControl
